import { Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateClient } from "@/features/clients/api";
import type { Client } from "@/features/clients/types";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { ApiError } from "@/lib/api";

const MAX_PRACTITIONERS = 6;

/**
 * Client-screen practitioner-assignment card: shows the practitioners assigned
 * to this client (index 0 = primary / lead) and lets staff change them after
 * creation. Saves by PATCHing the full `practitioner_ids` replace-set — the
 * server rebuilds the ordered links, so index 0 becomes the primary.
 */
export function ClientPractitionersCard({
  client,
  onClientUpdated,
}: {
  client: Client;
  onClientUpdated: (client: Client) => void;
}) {
  const [options, setOptions] = useState<PractitionerOption[]>([]);
  const [editing, setEditing] = useState(false);
  // Ordered slot ids being edited; slot 0 is the primary. "" = an empty slot.
  const [slots, setSlots] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [primaryError, setPrimaryError] = useState(false);

  useEffect(() => {
    let active = true;
    listPractitioners()
      .then((list) => active && setOptions(list))
      .catch(() => active && setOptions([]));
    return () => {
      active = false;
    };
  }, []);

  const assigned = client.practitioners ?? [];

  function startEdit() {
    const ids = assigned.map((p) => p.id);
    setSlots(ids.length ? ids : [""]);
    setPrimaryError(false);
    setEditing(true);
  }

  const setSlot = (slot: number, value: string) => {
    if (slot === 0) setPrimaryError(false);
    setSlots((prev) => prev.map((v, i) => (i === slot ? value : v)));
  };
  const addSlot = () => setSlots((prev) => [...prev, ""]);
  const removeSlot = (slot: number) => setSlots((prev) => prev.filter((_, i) => i !== slot));

  // Hide practitioners already picked in other slots so the same person can't be
  // assigned twice (mirrors the add-client form).
  const optionsFor = (slot: number) => {
    const takenElsewhere = new Set(slots.filter((p, i) => p && i !== slot));
    return options.filter((p) => !takenElsewhere.has(p.id));
  };

  async function save() {
    const ids = slots.filter(Boolean);
    if (ids.length === 0) {
      setPrimaryError(true);
      toast.error("Select a primary practitioner.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateClient(client.id, { practitioner_ids: ids });
      onClientUpdated(updated);
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update practitioners.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="gap-4 p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-foreground text-lg font-semibold">Practitioners</h3>
        {!editing && (
          <Button type="button" size="sm" variant="outline" onClick={startEdit}>
            Manage
          </Button>
        )}
      </div>

      {!editing ? (
        assigned.length === 0 ? (
          <p className="text-muted-foreground text-sm">No practitioners assigned.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {assigned.map((p, i) => (
              <Badge key={p.id} variant={i === 0 ? "default" : "secondary"}>
                {i === 0 ? `${p.name} · Primary` : p.name}
              </Badge>
            ))}
          </div>
        )
      ) : (
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>
              Primary practitioner <span className="text-destructive">*</span>
            </Label>
            <Select value={slots[0] || undefined} onValueChange={(v) => setSlot(0, v)}>
              <SelectTrigger className="w-full" aria-invalid={primaryError}>
                <SelectValue placeholder="Select the lead practitioner" />
              </SelectTrigger>
              <SelectContent>
                {optionsFor(0).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {primaryError && (
              <p className="text-destructive text-sm">Select a primary practitioner.</p>
            )}
          </div>

          {slots.slice(1).map((value, i) => {
            const slot = i + 1;
            return (
              <div className="flex items-center gap-2" key={slot}>
                <span className="text-muted-foreground w-4 text-sm">{slot + 1}</span>
                <Select value={value || undefined} onValueChange={(v) => setSlot(slot, v)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a practitioner" />
                  </SelectTrigger>
                  <SelectContent>
                    {optionsFor(slot).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove practitioner ${slot + 1}`}
                  onClick={() => removeSlot(slot)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            );
          })}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSlot}
              disabled={slots.length >= MAX_PRACTITIONERS}
            >
              <Plus className="size-4" /> Add practitioner
            </Button>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
